import API_BASE_URL from '../config';
import * as hazardGateway from '../db/hazardGateway';
import { classifyOutboxFailure } from '../db/outboxPolicy';
import type { OutboxOp } from '../db/hazardGateway';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import hazardService from './hazardService';
import { uploadFileViaSas } from './mediaUploadService';
import { assertStorageForFiles } from './syncUtils';

export { assertStorageForFiles };
export { backoffMs, classifyOutboxFailure, isBlockedByDependency, MAX_BACKOFF_MS } from '../db/outboxPolicy';
export type { OutboxDisposition } from '../db/outboxPolicy';

let draining = false;

/**
 * Single-tab drain. Hazard, comment, and reaction ops share one outbox.
 * Legacy reaction queues are copied in first so a queued op is not dropped.
 */
export async function drainOutbox(): Promise<void> {
  if (draining) {
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    await navigator.locks.request('safework-outbox', () => runDrain());
    return;
  }
  await runDrain();
}

async function runDrain(): Promise<void> {
  if (draining || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return;
  }
  draining = true;
  try {
    await hazardGateway.promoteLegacyQueues();
    await hazardService.pushUnsyncedHazards();
    await hazardService.pushUnsyncedComments();
    for (let pass = 0; pass < 3; pass += 1) {
      const ops = await hazardGateway.dueOps();
      const actionable = ops.filter((op) => !op.kind.startsWith('hazard.'));
      if (actionable.length === 0) {
        break;
      }
      for (const op of actionable) {
        await pushOp(op);
      }
    }
  } finally {
    draining = false;
  }
}

async function pushOp(op: OutboxOp): Promise<void> {
  try {
    if (op.kind.startsWith('comment.')) {
      await pushComment(op);
      return;
    }
    if (op.kind === 'reaction.add' || op.kind === 'reaction.remove') {
      await pushReaction(op);
    }
  } catch (error) {
    const disposition = classifyOutboxFailure({
      attempts: op.attempts,
      networkError: true,
    });
    await hazardGateway.applyOpResult(op.id, disposition, error instanceof Error ? error.message : 'network');
  }
}

async function uploadedRefs(op: OutboxOp, refs: string[]): Promise<string[]> {
  const media = await hazardGateway.opMedia(op.id);
  const byId = new Map(media.map((item) => [item.id, item]));
  const out: string[] = [];
  for (const ref of refs) {
    if (!ref.startsWith('media:')) {
      out.push(ref);
      continue;
    }
    const item = byId.get(ref.slice('media:'.length));
    if (!item) {
      continue;
    }
    const file = new File([item.blob], item.filename, { type: item.mimeType || item.blob.type });
    const { blobUrl } = await uploadFileViaSas({ file });
    out.push(blobUrl);
  }
  return out;
}

async function pushComment(op: OutboxOp): Promise<void> {
  const payload = op.payload;
  const id = typeof payload.id === 'string' ? payload.id : op.entityId;
  if (op.kind === 'comment.delete') {
    const ok = await sendJson(`${API_BASE_URL}/api/softDeleteComment`, 'POST', { id }, op);
    if (ok) {
      await hazardGateway.noteCommentSynced(id, true);
    }
    return;
  }
  const refs = Array.isArray(payload.image_data) ? payload.image_data.filter((item): item is string => typeof item === 'string') : [];
  const images = await uploadedRefs(op, refs);
  if (op.kind === 'comment.update') {
    const ok = await sendJson(`${API_BASE_URL}/api/updateComment`, 'PUT', {
      id,
      comment: payload.comment,
      added_images: images,
      deleted_image_indexes: Array.isArray(payload.deleted_image_indexes) ? payload.deleted_image_indexes : [],
      is_resolution: payload.is_resolution === true,
    }, op);
    if (ok) {
      await hazardGateway.noteCommentSynced(id);
    }
    return;
  }
  const ok = await sendJson(`${API_BASE_URL}/api/submitComment`, 'POST', {
    id,
    incident_id: payload.incident_id,
    comment: payload.comment,
    created_at: payload.created_at,
    updated_at: payload.updated_at ?? payload.created_at,
    images,
    is_resolution: payload.is_resolution === true,
  }, op);
  if (ok) {
    await hazardGateway.noteCommentSynced(id);
  }
}

async function pushReaction(op: OutboxOp): Promise<void> {
  const payload = op.payload;
  const contentType = payload.content_type === 'comment' ? 'comment' : 'card';
  const contentId = typeof payload.content_id === 'string' ? payload.content_id : '';
  const reactionType = typeof payload.reaction_type_name === 'string' ? payload.reaction_type_name : '';
  const id = typeof payload.id === 'string' ? payload.id : op.entityId;
  if (op.kind === 'reaction.remove') {
    const body = contentType === 'card'
      ? { hazardId: contentId, reactionType, id }
      : { commentId: contentId, reactionType, reactionId: id };
    const path = contentType === 'card' ? '/api/removeHazardReaction' : '/api/removeCommentReaction';
    const ok = await sendJson(`${API_BASE_URL}${path}`, 'POST', body, op);
    if (ok) {
      await hazardGateway.noteReactionSynced(id, true);
    }
    return;
  }
  const body = contentType === 'card'
    ? { hazardId: contentId, reactionType, id }
    : { commentId: contentId, reactionType, id };
  const path = contentType === 'card' ? '/api/addHazardReaction' : '/api/addCommentReaction';
  const ok = await sendJson(`${API_BASE_URL}${path}`, 'POST', body, op);
  if (ok) {
    await hazardGateway.noteReactionSynced(id, false);
  }
}

async function sendJson(
  url: string,
  method: 'POST' | 'PUT',
  body: Record<string, unknown>,
  op: OutboxOp
): Promise<boolean> {
  let response: Response;
  try {
    response = await fetchWithAuth(url, {
      method,
      body: JSON.stringify(body),
    });
  } catch (error) {
    const disposition = classifyOutboxFailure({ attempts: op.attempts, networkError: true });
    await hazardGateway.applyOpResult(op.id, disposition, error instanceof Error ? error.message : 'network');
    return false;
  }
  if (response.ok) {
    await hazardGateway.completeOp(op.id);
    return true;
  }
  const text = await response.text();
  const disposition = classifyOutboxFailure({
    status: response.status,
    body: text,
    attempts: op.attempts,
  });
  await hazardGateway.applyOpResult(
    op.id,
    disposition,
    disposition === 'pause' ? undefined : `HTTP_${response.status}`
  );
  return false;
}
